import React, {
    useState, useEffect
} from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ferris/react/components/button';

const NextButton = (props) => {
    const {
        moveOn,
        allFieldsValid,
        allServerFieldsValid,
        geocoderValidation,
        geocoderLatLng,
        serverAddressValidation,
        unitNumberValidation,
        triggerGeoCoderValidation,
        buttonLabel,
        isGeoCoderEnabled,
        triggerValidateUnitNumber,
        unitCheckEnabled,
        confirmNoUnitNumber,
        trigger,
        canBypassValidation,
        confirmsCorrectAddress,
        isAddressV2
    } = props;

    const [isValidating, setIsValidating] = useState(false);

    const handleNextClick = () => {
        setIsValidating(true);
        if (isAddressV2) {
            if (unitCheckEnabled && !confirmNoUnitNumber) {
                triggerValidateUnitNumber();
                return;
            }
            moveOn();
            return;
        }
        if (allFieldsValid && allServerFieldsValid) {
            trigger('genericClick', {
                category: 'navigation',
                action: 'Manual Location: nextButton'
            });
            if (canBypassValidation && confirmsCorrectAddress) {
                moveOn();
                return;
            }
            if (unitCheckEnabled && !confirmNoUnitNumber) {
                triggerValidateUnitNumber();
                return;
            }
            if (isGeoCoderEnabled) {
                triggerGeoCoderValidation();
                return;
            }
            moveOn();
        } else {
            setIsValidating(false);
        }
    };
    const {
        value: isGeoCoderValid,
        success: geocoderValidationSuccess,
        fail: geocoderValidationFail
    } = geocoderValidation;

    // here it will wait for other validation in the future
    useEffect(() => {
        if (isValidating) {
            if (
                isGeoCoderValid
                    && (geocoderValidationSuccess || geocoderValidationFail)
            ) {
                moveOn();
            }
            if (geocoderValidationSuccess || geocoderValidationFail) {
                setIsValidating(false);
            }
        }
    }, [
        isGeoCoderValid,
        geocoderValidationSuccess,
        geocoderValidationFail
    ]);

    const {
        value: missingUnitNumber,
        success: unitValidationSuccess,
        fail: unitValidationFail
    } = unitNumberValidation;

    // here it will wait for other validation in the future
    useEffect(() => {
        if (isValidating && (unitValidationSuccess || unitValidationFail)) {
            if (!missingUnitNumber.missingUnitNumber && missingUnitNumber.unitNumberValid) {
                if (isAddressV2) {
                    moveOn();
                } else {
                    triggerGeoCoderValidation();
                }
            } else {
                setIsValidating(false);
            }
        }
    }, [
        missingUnitNumber,
        unitValidationSuccess,
        unitValidationFail
    ]);

    const {
        value: geoCode,
        success: geocoderLatLngSuccess
    } = geocoderLatLng;

    // eslint-disable-next-line max-len
    const validGeoCode = () => (geocoderLatLngSuccess && geoCode.lat !== null && geoCode.lng != null)
        || (canBypassValidation && confirmsCorrectAddress)
        || !isGeoCoderEnabled;
    // eslint-disable-next-line max-len
    const buttonEnabled = () => (
        allFieldsValid
            && (
                (allServerFieldsValid && (!isGeoCoderEnabled || isGeoCoderValid))
                    || (canBypassValidation && confirmsCorrectAddress))
    )
        && !isValidating
        && validGeoCode()
        && (isAddressV2 || serverAddressValidation.pinMap.isValid || !isGeoCoderEnabled);

    const ButtonToUse = Button;
    return (
        <ButtonToUse
            isFullWidth
            classes="submitBtn"
            htmlButtonType="button"
            type="primary"
            data-wdio="manual-location-next-button"
            id="manualAddressNextBtn"
            onClick={handleNextClick}
            disabled={!buttonEnabled()}
        >
            {buttonLabel}
        </ButtonToUse>
    );
};

NextButton.propTypes = {
    canBypassValidation: PropTypes.bool.isRequired,
    confirmsCorrectAddress: PropTypes.bool.isRequired,
    isGeoCoderEnabled: PropTypes.bool.isRequired,
    triggerGeoCoderValidation: PropTypes.func.isRequired,
    geocoderLatLng: PropTypes.shape({
        value: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number
        }),
        errorList: PropTypes.string,
        start: PropTypes.bool.isRequired,
        success: PropTypes.bool.isRequired,
        fail: PropTypes.bool.isRequired
    }).isRequired,
    geocoderValidation: PropTypes.shape({
        value: PropTypes.bool,
        errorList: PropTypes.arrayOf(PropTypes.string),
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    unitNumberValidation: PropTypes.shape({
        value: PropTypes.bool,
        start: PropTypes.bool,
        success: PropTypes.bool,
        fail: PropTypes.bool
    }).isRequired,
    triggerValidateUnitNumber: PropTypes.func.isRequired,
    unitCheckEnabled: PropTypes.bool.isRequired,
    serverAddressValidation: PropTypes.shape({
        address1: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        address2: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        city: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        stateProvince: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        postalCode: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        country: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        }),
        generalState: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            errorList: PropTypes.arrayOf(PropTypes.string)
        }),
        pinMap: PropTypes.shape({
            isValid: PropTypes.bool.isRequired,
            error: PropTypes.string
        })
    }).isRequired,
    confirmNoUnitNumber: PropTypes.bool.isRequired,
    buttonLabel: PropTypes.string.isRequired,
    allFieldsValid: PropTypes.bool.isRequired,
    allServerFieldsValid: PropTypes.bool.isRequired,
    moveOn: PropTypes.func.isRequired,
    trigger: PropTypes.func.isRequired,
    isAddressV2: PropTypes.bool.isRequired
};

export default NextButton;
